import { inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpInterceptorFn } from '@angular/common/http';
import { BehaviorSubject, catchError, EMPTY, filter, from, switchMap, take, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { CsrfService } from '../services/csrf.service';
import { NotificationService } from '../services/notification.service';
import { RefreshResponse } from '../models/auth.models';
import { environment } from '../../../environments/environment';

const PUBLIC_URLS = [
  '/auth/login',
  '/auth/public-key',
  '/auth/password/forgot',
  '/auth/password/reset',
  '/management/invitations/validate',
  '/management/invitations/complete',
  '/management/purchase-orders/public',
];

const MUTATION_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

// Sentinel emitido cuando el refresh falla — cierra los requests en cola sin errores.
const REFRESH_FAILED = '__REFRESH_FAILED__';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // PRUEBA: storage.api es un microservicio aparte que ya se autentica con su propio
  // header x-signature (ver StorageService) — no necesita Bearer ni cookies de
  // plataforma. Mandarle withCredentials:true fuerza al navegador a exigir un CORS
  // que no sea '*', y ahí truena. Si esto arregla la petición, el fix real es dejar
  // este guard (o su equivalente) permanente.
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  const auth    = inject(AuthService);
  const router  = inject(Router);
  const backend = inject(HttpBackend);
  const notify  = inject(NotificationService);
  const csrf    = inject(CsrfService);

  const isPublic   = PUBLIC_URLS.some(url => req.url.includes(url));
  const token      = auth.getToken();
  const isMutation = MUTATION_METHODS.has(req.method);

  // Builds the cloned request with withCredentials, Bearer and optional CSRF header.
  // withCredentials sends sof_platform_* cookies; Bearer stays in parallel during transition.
  // Si el caller ya puso su propio Authorization (ej. CompletarOrdenComponent.loginYPagar,
  // que maneja su propio token porque esta página nunca abre sesión vía AuthService), no lo
  // pisamos — setHeaders reemplaza el header, así que solo lo fijamos si no vino ya explícito.
  const cloneReq = (accessToken: string | null, csrfToken?: string | null) => {
    const headers: Record<string, string> = {};
    if (!isPublic && accessToken && !req.headers.has('Authorization')) headers['Authorization'] = `Bearer ${accessToken}`;
    if (csrfToken)                headers['X-CSRF-Token']  = csrfToken;
    return req.clone({ withCredentials: true, setHeaders: headers });
  };

  const handleSessionExpired = () => {
    auth.logout();
    notify.error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.', 'Sesión expirada');
    void router.navigate(['/login']);
  };

  const dispatch = (accessToken: string | null, csrfToken?: string | null) =>
    next(cloneReq(accessToken, csrfToken)).pipe(
      catchError(err => {
        if (err.status === 403) {
          notify.error('No tienes permiso para realizar esta acción.', 'Acceso denegado');
          return throwError(() => err);
        }

        if (err.status !== 401 || isPublic) return throwError(() => err);

        // Requests en cola — esperan el resultado del refresh en curso.
        if (isRefreshing) {
          return refreshTokenSubject.pipe(
            filter((t): t is string => t !== null),
            take(1),
            switchMap(t => {
              if (t === REFRESH_FAILED) return EMPTY;
              return next(cloneReq(t, csrfToken));
            }),
          );
        }

        const refreshToken = auth.getRefreshToken();
        if (!refreshToken) {
          handleSessionExpired();
          return EMPTY;
        }

        isRefreshing = true;
        refreshTokenSubject.next(null);

        const cachedCsrf     = csrf.getCachedToken();
        const refreshHeaders = new HttpHeaders(cachedCsrf ? { 'X-CSRF-Token': cachedCsrf } : {});

        // Usa HttpBackend directamente para evitar el interceptor (no CSRF, no deadlock).
        const direct = new HttpClient(backend);
        return direct.post<RefreshResponse>(
          `${environment.apiUrl}/auth/refresh`,
          { refreshToken },
          { withCredentials: true, headers: refreshHeaders },
        ).pipe(
          switchMap(tokens => {
            isRefreshing = false;
            auth.setAccessToken(tokens.accessToken);
            auth.setRefreshToken(tokens.refreshToken);
            refreshTokenSubject.next(tokens.accessToken);
            return next(cloneReq(tokens.accessToken, csrfToken));
          }),
          catchError(() => {
            isRefreshing = false;
            refreshTokenSubject.next(REFRESH_FAILED);
            handleSessionExpired();
            return EMPTY;
          }),
        );
      }),
    );

  // Mutations fetch the CSRF token lazily (deduped by CsrfService) before dispatching.
  if (!isPublic && isMutation) {
    return from(csrf.ensureToken()).pipe(
      switchMap(csrfToken => dispatch(token, csrfToken)),
    );
  }

  return dispatch(token);
};
